import type { Product } from "@/types";
import CollapsibleBody from "@/components/base/CollapsibleBody";
import { formatCOP } from "@/utils/format";

interface ErrorRow {
  product: Product;
  ratio: number; // costo / precio público × 100
  suggested: number; // costo sugerido = 75% del precio público
}

// Productos con el costo cargado igual o por encima del precio público.
// Son errores de digitación (no se normalizan solos) y se corrigen a mano en Catálogo.
const buildRows = (products: Product[]): ErrorRow[] =>
  products
    .filter((p) => p.pricePublic > 0 && p.costBase >= p.pricePublic)
    .map((p) => ({
      product: p,
      ratio: (p.costBase / p.pricePublic) * 100,
      suggested: Math.round(p.pricePublic * 0.75),
    }))
    .sort((a, b) => b.ratio - a.ratio);

const CostErrorPanel = ({ products }: { products: Product[] }) => {
  const rows = buildRows(products);

  return (
    <div className="glass-panel rounded-lg p-4 md:p-5">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-8 h-8 rounded-md bg-accent-500/25 text-accent-100 flex items-center justify-center">
          <i className="ri-error-warning-line" />
        </span>
        <h3 className="editable-title text-base font-bold">Errores de costo (costo ≥ precio)</h3>
        {rows.length > 0 && (
          <span className="ml-auto text-[11px] font-bold rounded-full bg-accent-500/20 text-accent-100 px-2.5 py-0.5">
            {rows.length}
          </span>
        )}
      </div>
      <p className="text-[11px] text-foreground-500 mb-4">
        Estos productos tienen el costo cargado <span className="text-foreground-300">igual o por encima</span> del precio
        público: casi siempre es un error de digitación. Se dejan intactos (no se normalizan) para que los corrijas a mano en{" "}
        <span className="text-foreground-300">Catálogo</span>. El resto del catálogo ya quedó normalizado al 75% del precio.
      </p>

      {rows.length === 0 ? (
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-6 text-center">
          <i className="ri-checkbox-circle-line text-xl text-secondary-200" />
          <p className="text-xs text-foreground-500 mt-2">
            Ningún producto tiene el costo por encima del precio. Catálogo impecable.
          </p>
        </div>
      ) : (
        <CollapsibleBody count={rows.length} threshold={6} unit="productos">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-foreground-500">
                  <th className="py-2 pr-3 font-semibold">Producto</th>
                  <th className="py-2 px-3 font-semibold text-right">Precio público</th>
                  <th className="py-2 px-3 font-semibold text-right">Costo cargado</th>
                  <th className="py-2 px-3 font-semibold text-right">% del precio</th>
                  <th className="py-2 pl-3 font-semibold text-right">Costo sugerido (75%)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.product.id || r.product.sku} className="border-t border-background-300/40">
                    <td className="py-2.5 pr-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-foreground-900 max-w-[220px] truncate">
                          {r.product.name}
                        </span>
                        <span className="text-[9px] font-bold rounded-full bg-accent-500/25 text-accent-100 px-2 py-0.5 whitespace-nowrap">
                          Corregir
                        </span>
                      </div>
                      <span className="block font-mono text-[10px] text-accent-200 mt-0.5">{r.product.sku}</span>
                    </td>
                    <td className="py-2.5 px-3 text-right text-xs text-foreground-700 whitespace-nowrap">
                      {formatCOP(r.product.pricePublic)}
                    </td>
                    <td className="py-2.5 px-3 text-right text-xs font-bold text-accent-200 whitespace-nowrap">
                      {formatCOP(r.product.costBase)}
                    </td>
                    <td className="py-2.5 px-3 text-right text-xs font-bold text-accent-200 whitespace-nowrap">
                      {r.ratio.toFixed(0)}%
                    </td>
                    <td className="py-2.5 pl-3 text-right text-xs font-semibold text-secondary-200 whitespace-nowrap">
                      {formatCOP(r.suggested)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleBody>
      )}
    </div>
  );
};

export default CostErrorPanel;